import { useMemo } from 'react';

import { rpaBridge } from '@heidi/plugin-rpa';

import type {
  UseRecordedWorkflowSkills,
  UseWorkflowSkillRoutines,
  WorkflowSkill,
  WorkflowSkillWithKind,
} from '../../../contracts';
import { useAvailableWorkflowSkillsWithWorkflowsCore } from '../../../data/projects/hooks/useAvailableWorkflowSkills';
import { getWorkHostPort } from '../../work-home/work-home-ports';

/** The Workflows verdict and the routine list, read through the app's gated hook. */
const useWorkflowSkillRoutines: UseWorkflowSkillRoutines = ({ enabled }) => {
  const isWorkflowsEnabled = getWorkHostPort('shell').useIsWorkflowsEnabled();
  const { data } = getWorkHostPort('routines').useWorkflows({}, { enabled });
  return { isWorkflowsEnabled, data } as ReturnType<UseWorkflowSkillRoutines>;
};

/** The recorder's ready skills while it is on and the desktop bridge answers. */
const useRecordedWorkflowSkills: UseRecordedWorkflowSkills = ({ enabled }) => {
  const desktop = getWorkHostPort('desktop');
  const isRecorderEnabled = getWorkHostPort('shell').useIsRpaRecorderEnabled();
  const { data } = desktop.useUserWorkflows({ enabled });
  const rpaAvailable = isRecorderEnabled && rpaBridge.isAvailable();

  return useMemo(
    () =>
      (rpaAvailable
        ? desktop.deriveAvailableWorkflowSkillsFromSummaries([
            ...(data?.personal ?? []),
            ...(data?.team ?? []),
          ])
        : []) as WorkflowSkill[],
    [rpaAvailable, data, desktop]
  );
};

/** All enabled workflows (instructed + recorded) for the slash picker. */
export const useAvailableWorkflowSkillsWithWorkflows = (
  options: { enabled?: boolean } = {}
): readonly WorkflowSkillWithKind[] =>
  useAvailableWorkflowSkillsWithWorkflowsCore(
    useWorkflowSkillRoutines,
    useRecordedWorkflowSkills,
    options
  );
